import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSkinSession } from '../context/SkinSessionContext';
import { clearSession, getSessionId, finalizeCustomerSession } from '../utils/sessionManager';
import {
  SALON_APP_BASE,
  SALON_HUB_PATH,
} from '../config/salonApp';
import { getApiBaseUrl } from '../utils/apiConfig';
import { performSalonLogout } from '../utils/salonAuth';
import { getSalonModalRoot } from '../../shared/modalRoots';
import './SalonHeaderActions.css';

function HomeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

const SalonHeaderActions = ({
  homeButtonClassName,
  logoutButtonClassName,
  onHome,
}) => {
  const navigate = useNavigate();
  const { setToken, token } = useSkinSession();
  const [showConfirm, setShowConfirm] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const resolvedLogoutButtonClass = useMemo(
    () => logoutButtonClassName || homeButtonClassName,
    [logoutButtonClassName, homeButtonClassName]
  );

  const handleHome = useCallback(() => {
    if (typeof onHome === 'function') {
      onHome();
      return;
    }
    navigate(SALON_HUB_PATH);
  }, [navigate, onHome]);

  const handleConfirmLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setLogoutError('');

    const sid = getSessionId();
    const API_BASE_URL = getApiBaseUrl() || '';
    if (sid) {
      await finalizeCustomerSession(API_BASE_URL, sid, token || null);
    }

    const serverResult = await performSalonLogout();
    if (!serverResult.ok) {
      setLogoutError(serverResult.error || 'Logout failed. Please try again.');
      setIsLoggingOut(false);
      return;
    }

    try {
      setToken('');
    } catch (_) {}
    try {
      clearSession();
    } catch (_) {}

    setShowConfirm(false);
    setIsLoggingOut(false);
    navigate(`${SALON_APP_BASE}/login`, { replace: true });
  }, [isLoggingOut, navigate, setToken, token]);

  const confirmationDialog = showConfirm ? createPortal(
    <div className="salon-logout-modal-overlay" role="presentation" onClick={() => setShowConfirm(false)}>
      <div
        className="salon-logout-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="salon-logout-title"
        aria-describedby="salon-logout-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="salon-logout-modal-icon" aria-hidden="true">
          <LogoutIcon />
        </div>
        <h3 id="salon-logout-title" className="salon-logout-modal-title">Confirm logout</h3>
        <p id="salon-logout-description" className="salon-logout-modal-description">
          Are you sure you want to log out from this salon session?
        </p>
        {logoutError ? (
          <p className="salon-logout-modal-error" role="alert">
            {logoutError}
          </p>
        ) : null}
        <div className="salon-logout-modal-actions">
          <button
            type="button"
            className="salon-logout-modal-button salon-logout-modal-button-secondary"
            onClick={() => setShowConfirm(false)}
            disabled={isLoggingOut}
          >
            Cancel
          </button>
          <button
            type="button"
            className="salon-logout-modal-button salon-logout-modal-button-primary"
            onClick={handleConfirmLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? 'Logging out…' : 'Logout'}
          </button>
        </div>
      </div>
    </div>,
    getSalonModalRoot()
  ) : null;

  return (
    <>
      <div className="salon-header-actions" role="group" aria-label="Header actions">
        <button
          type="button"
          className={`${resolvedLogoutButtonClass} salon-logout-icon-button`}
          onClick={() => {
            setLogoutError('');
            setShowConfirm(true);
          }}
          aria-label="Logout"
          title="Logout"
        >
          <LogoutIcon />
        </button>
        <button
          type="button"
          className={homeButtonClassName}
          onClick={handleHome}
          aria-label="Go to home"
        >
          <HomeIcon />
        </button>
      </div>
      {confirmationDialog}
    </>
  );
};

export default SalonHeaderActions;
